import React, { useEffect, useState } from 'react';
import { Card, Col, Input, Row, Typography, Spin, Tooltip, Space, Tag, Divider, Statistic } from 'antd';
import { baseurl } from '../../components/lib/settings';
import { format } from 'date-fns';
import { LoadingOutlined, CheckCircleOutlined, DollarOutlined, SearchOutlined, BarChartOutlined, ClockCircleOutlined, DownCircleFilled, CloseOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Meta } = Card;
const { Search } = Input;
const { Title, Text } = Typography;

function AdminAmountReportsByDelegationCompany() {
  const [groupedData, setGroupedData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      const resp = await baseurl.post("v3/ngoma/administrator/financing/by_editions/");
      const data = resp.data;
      const groupedByYear = data.reduce((acc, item) => {
        const year = new Date(item.first_vote_date).getFullYear();
        if (!acc[year]) acc[year] = [];
        acc[year].push(item);
        return acc;
      }, {});

      setGroupedData(groupedByYear);
      setFilteredData(groupedByYear);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 60000); // 1 minuto
    return () => clearInterval(intervalId);
  }, []);

  const formatToMetical = (amount) => {
    return (amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'MZN' });
  };

  const formatStartDate = (formatDate) => {
    if (!formatDate) return "";
    return format(new Date(formatDate), "dd/MM/yyyy");
  };

  const formatEndDate = (formatDate) => {
    if (!formatDate) return "";
    return format(new Date(formatDate), "dd/MM/yyyy");
  };

  const handleSearch = (value) => {
    setSearchTerm(value.toLowerCase());
    const filtered = {};
    Object.keys(groupedData).forEach(year => {
      filtered[year] = (groupedData[year] || []).filter(item =>
        item.delegation_description && item.delegation_description.toLowerCase().includes(value.toLowerCase())
      );
    });
    setFilteredData(filtered);
  };

  return (
    <div style={{ padding: "10px", background: "#f3f4f6", minHeight: "100vh" }}>
      <div style={{ marginBottom: "32px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
        <Space direction="vertical" size={4}>
          <Title level={3} style={{ margin: 0, color: "#111827", fontWeight: 700 }}>
            <BarChartOutlined style={{ color: "#1890ff", marginRight: "12px" }} />
            Relatório de Faturação
          </Title>
          <Text type="secondary" style={{ fontSize: "15px" }}>
            Acompanhe o total acumulado por delegação e detalhe financeiro das operadoras.
          </Text>
        </Space>
        <Input
          placeholder="Pesquisar delegação..."
          prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: "300px", borderRadius: "24px", padding: "8px 16px" }}
          size="large"
        />
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "#1890ff" }} spin />} />
        </div>
      ) : (
        <>
          {Object.keys(filteredData).sort((a, b) => b - a).map(year => {
            const sortedItems = [...filteredData[year]].sort(
              (a, b) => (b.delegation_total_amount || 0) - (a.delegation_total_amount || 0)
            );

            const yearTotal = sortedItems.reduce((acc, item) => acc + (item.delegation_total_amount || 0), 0);
            const mpesaTotal = sortedItems.reduce((acc, item) => acc + (item.operator_totals?.mpesa || 0), 0);
            const emolaTotal = sortedItems.reduce((acc, item) => acc + (item.operator_totals?.emola || 0), 0);
            const mkeshTotal = sortedItems.reduce((acc, item) => acc + (item.operator_totals?.mkesh || 0), 0);

            return (
              <div key={year} style={{ marginBottom: "40px" }}>
                <Card 
                  bordered={false} 
                  style={{ borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", marginBottom: "24px", background: "#d6dbdb" }}
                  bodyStyle={{ padding: "10px" }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: "16px" }}>
                    <Space direction="vertical" size={2}>
                      <Title level={4} style={{ margin: 0, color: "#1f2937", fontWeight: 700 }}>Resultados de {year}</Title>
                      <Space size="large" style={{ marginTop: "8px" }}>
                        {/* <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: "12px", padding: "2px 10px" }}>
                          Visão Consolidada
                        </Tag> */}
                        <Text type="secondary"><ClockCircleOutlined /> Os dados incluem atividade nas últimas 24h</Text>
                      </Space>
                    </Space>
                    
                    <div style={{ textAlign: "right" }}>
                      {/* <Text type="secondary" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px" }}>Total Anual</Text> */}
                      <div style={{ fontSize: "21px", fontWeight: 800, color: "#000306" }}>
                        {formatToMetical(yearTotal)}
                      </div>
                      <Space size="middle" style={{ marginTop: "0px" }}>
                        <span style={{ fontSize: "12px" }}><span style={{ color: "#ef4444", fontWeight: 600 }}>●</span> M-Pesa: {formatToMetical(mpesaTotal)}</span>
                        <span style={{ fontSize: "12px" }}><span style={{ color: "#f97316", fontWeight: 600 }}>●</span> E-mola: {formatToMetical(emolaTotal)}</span>
                      </Space>
                    </div>
                  </div>
                  <div style={{marginBottom: '20px'}}>
                    
                  </div>
                  <Row gutter={[24, 24]}>
                  {sortedItems.map((item, index) => (
                    <Col key={index} xs={24} sm={12} md={8} lg={6}>
                      <Card
                        hoverable
                        style={{ borderRadius: "16px", height: "100%", display: "flex", flexDirection: "column", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
                        bodyStyle={{ padding: "12px", flex: 1, display: "flex", flexDirection: "column" }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: "16px" }}>
                          <Title level={5} style={{ margin: 0, color: "#111827", lineHeight: 1.3 }}>
                            {item.delegation_description || "Delegação Desconhecida"}
                          </Title>
                          <Tag color={item.is_active ? "green" : "default"} style={{ borderRadius: "12px", margin: 0, border: "none" }}>
                            {item.is_active ? "Em Curso" : "Encerrado"}
                          </Tag>
                        </div>
                        
                        <div style={{ marginBottom: "16px", flex: 1 }}>
                          <Text type="secondary" style={{ display: "block", marginBottom: "8px", fontSize: "13px" }}>
                            {item.edition_description || "Sem descrição"}
                          </Text>
                          <div style={{ background: "#f8fafc", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", color: "#6b7280" }}>
                            <ClockCircleOutlined style={{ marginRight: "6px" }} />
                            {formatStartDate(item.first_vote_date)} a {formatEndDate(item.last_vote_date)}
                          </div>
                        </div>

                        <Divider style={{ margin: "12px 0" }} />

                        <div style={{ marginBottom: "16px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px" }}>
                            <Text type="secondary">M-Pesa</Text>
                            <Text strong>{formatToMetical(item.operator_totals?.mpesa)}</Text>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px" }}>
                            <Text type="secondary">E-mola</Text>
                            <Text strong>{formatToMetical(item.operator_totals?.emola)}</Text>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                            <Text type="secondary">Últimas 24h</Text>
                            <Text strong style={{ color: "#10b981" }}>+{formatToMetical(item.delegation_last_24h_amount)}</Text>
                          </div>
                        </div>

                        <div style={{ background: "#f0fdf4", padding: "5px", borderRadius: "12px", border: "1px solid #dcfce7" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Text style={{ color: "#166534", fontSize: "12px", fontWeight: 500, textTransform: "uppercase" }}><Title level={5} style={{ margin: 0, marginTop: "0px", color: "#15803d" }}>
                            {formatToMetical(item.delegation_total_amount)}
                          </Title></Text>
                            <Tooltip title={item.edition_disbursement ? "O valor desta edição já foi pago" : "Aguardando desembolso"}>
                              {/* {item.edition_disbursement && <CheckCircleOutlined style={{ color: "#15803d", fontSize: "16px" }} />} */}
                              {item.edition_disbursement ? <><CheckCircleOutlined style={{ color: "#15803d", fontSize: "16px" }} /></> : <><CloseCircleOutlined style={{ color: "red", fontSize: "16px" }} /></>}
                            </Tooltip>
                          </div>
                          {/* <Title level={5} style={{ margin: 0, marginTop: "0px", color: "#15803d" }}>
                            {formatToMetical(item.delegation_total_amount)}
                          </Title> */}
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
                </Card>

                
              </div>
            );
          })}
        </>
      )}
      <style>{`
        .ant-card-hoverable:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.08) !important;
        }
      `}</style>
    </div>
  );
}

export default AdminAmountReportsByDelegationCompany;